'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import ArrowLeftIcon from '@/components/assets/icon_arrow_left.svg';
import SearchInputField from '@/components/SearchInputField';
import { SEARCH_PARAM_KEYS } from '@/constants/search';
import { useWorldSearchContext } from '@/context/WorldSearchContext';
import { useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import IconTool from '@/public/images/worlds/icon_tool.svg';

/**
 * Search controls rendered inside the ViverseApp top nav on the search route.
 *
 * The back and filter buttons are conditional siblings of the input rather than a separate
 * branch, so the input element keeps its identity (and focus) when a search becomes active.
 */
export default function AppTopNavSearch() {
  const $ = _c(40);
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    startSearch,
    searchValue: contextSearchValue,
    setSearchValue,
    appShowFilters,
    setAppShowFilters,
    appSearchCleared,
    setAppSearchCleared
  } = useWorldSearchContext();
  const [isSearchAnimating, setIsSearchAnimating] = useState(false);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(SEARCH_PARAM_KEYS.KEYWORD);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const urlKeyword = t0;
  const searchValue = appSearchCleared ? "" : contextSearchValue || urlKeyword || "";
  const isActiveSearch = !!urlKeyword && !appSearchCleared;
  let t1;
  if ($[2] !== searchValue || $[3] !== setAppSearchCleared || $[4] !== startSearch) {
    t1 = () => {
      const trimmed = searchValue.trim();
      if (!trimmed) {
        return;
      }
      setAppSearchCleared(false);
      setIsSearchAnimating(true);
      startSearch(trimmed, {
        onAfterNavigate: () => setIsSearchAnimating(false)
      });
    };
    $[2] = searchValue;
    $[3] = setAppSearchCleared;
    $[4] = startSearch;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSearch = t1;
  let t2;
  if ($[6] !== setAppSearchCleared || $[7] !== setSearchValue || $[8] !== urlKeyword) {
    t2 = value => {
      setSearchValue(value);
      if (urlKeyword && !value) {
        setAppSearchCleared(true);
      } else {
        if (value) {
          setAppSearchCleared(false);
        }
      }
    };
    $[6] = setAppSearchCleared;
    $[7] = setSearchValue;
    $[8] = urlKeyword;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleValueChange = t2;
  let t3;
  if ($[10] !== setAppSearchCleared || $[11] !== setSearchValue) {
    t3 = () => {
      setSearchValue("");
      setAppSearchCleared(true);
    };
    $[10] = setAppSearchCleared;
    $[11] = setSearchValue;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleClear = t3;
  let t4;
  if ($[13] !== router || $[14] !== setAppSearchCleared || $[15] !== setAppShowFilters || $[16] !== setSearchValue) {
    t4 = () => {
      setSearchValue("");
      setAppShowFilters(false);
      setAppSearchCleared(false);
      router.push("/");
    };
    $[13] = router;
    $[14] = setAppSearchCleared;
    $[15] = setAppShowFilters;
    $[16] = setSearchValue;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleBack = t4;
  let t5;
  if ($[18] !== handleBack || $[19] !== isActiveSearch || $[20] !== t) {
    t5 = isActiveSearch ? <button type="button" aria-label={t("back-button")} className="shrink-0 cursor-pointer" onClick={handleBack}><ArrowLeftIcon className="stroke-vive-primary-20 size-5 w-auto" /></button> : null;
    $[18] = handleBack;
    $[19] = isActiveSearch;
    $[20] = t;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t) {
    t6 = t("worlds-landing.search.placeholder");
    $[22] = t;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== handleClear || $[25] !== handleSearch || $[26] !== handleValueChange || $[27] !== isSearchAnimating || $[28] !== searchValue || $[29] !== t6) {
    t7 = <SearchInputField value={searchValue} placeholder={t6} isAnimating={isSearchAnimating} onValueChange={handleValueChange} onSearch={handleSearch} onClear={handleClear} />;
    $[24] = handleClear;
    $[25] = handleSearch;
    $[26] = handleValueChange;
    $[27] = isSearchAnimating;
    $[28] = searchValue;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== appShowFilters || $[32] !== isActiveSearch || $[33] !== setAppShowFilters || $[34] !== t) {
    t8 = isActiveSearch ? <button type="button" aria-label={t("worlds-landing.search.filters")} aria-pressed={appShowFilters} className={cn("grid size-[34px] shrink-0 cursor-pointer place-items-center rounded-full transition-colors", {
      "bg-vive-primary-70/60": appShowFilters
    })} onClick={() => setAppShowFilters(_temp)}><IconTool className="stroke-vive-primary-20 my-auto size-auto p-1.5" /></button> : null;
    $[31] = appShowFilters;
    $[32] = isActiveSearch;
    $[33] = setAppShowFilters;
    $[34] = t;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t5 || $[37] !== t7 || $[38] !== t8) {
    t9 = <>{t5}{t7}{t8}</>;
    $[36] = t5;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}
function _temp(prev) {
  return !prev;
}